import { useState } from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import { StarRegular, StarFilled } from '@fluentui/react-icons';
import { PageHeader } from '../../common/PageHeader';
import { DataTable } from '../../common/DataTable';
import { LoadingState } from '../../common/LoadingState';
import { ErrorState } from '../../common/ErrorState';
import { useCatalog } from '../../../hooks/useCatalog';
import { useFavorites } from '../../../hooks/useFavorites';
import { CatalogFilters } from './CatalogFilters';
import { useAuthContext } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services/api/apiClient';

export const CatalogPage = () => {
  const { catalogItems, loading, error, search, setSearch, category, setCategory, availability, setAvailability, reload, refreshCatalog } = useCatalog();
  const { toggle, isFavorite } = useFavorites();
  const { acquirePricingToken } = useAuthContext();
  const [syncingPricing, setSyncingPricing] = useState(false);
  const [pricingMessage, setPricingMessage] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const handleSyncPricing = async () => {
    setSyncingPricing(true);
    setPricingMessage('');
    try {
      const pricingToken = await acquirePricingToken();
      const res = await apiClient.post<{ ok: boolean; count: number }>('/api/catalog/sync-pricing', { pricingToken });
      setPricingMessage(`✓ Synced ${res.count} prices`);
      void reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setPricingMessage(`✗ ${msg}`);
    } finally {
      setSyncingPricing(false);
    }
  };

  const categories = Array.from(new Set(catalogItems.map((item) => item.cr_crm_category)));

  const displayedItems = (favoritesOnly
    ? catalogItems.filter((item) => isFavorite(item.cr_crm_catalogcacheid))
    : catalogItems
  ).filter((item) => item.cr_crm_erppriceusd > 0);

  if (loading) return <LoadingState label="Loading catalog..." />;
  if (error) return <ErrorState message={error} onRetry={() => void reload()} />;

  return (
    <div>
      <PageHeader title="Catalog" subtitle="Partner Center NCE products synced from Microsoft — prices updated automatically" actions={[{ label: 'Refresh cache', onClick: () => void refreshCatalog(), appearance: 'primary' }]} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Button appearance="secondary" onClick={() => void handleSyncPricing()} disabled={syncingPricing}>
          {syncingPricing ? <><Spinner size="tiny" />&nbsp;Syncing prices...</> : 'Sync Pricing (NCE)'}
        </Button>
        {pricingMessage && <span style={{ fontSize: 13 }}>{pricingMessage}</span>}
      </div>
      <CatalogFilters search={search} setSearch={setSearch} category={category} setCategory={setCategory} availability={availability} setAvailability={setAvailability} favoritesOnly={favoritesOnly} setFavoritesOnly={setFavoritesOnly} categories={categories} />
      <div style={{ overflowX: 'auto' }}>
      <DataTable
        items={displayedItems}
        columns={[
          { columnId: 'fav', label: '⭐', renderCell: (item) => (
            <button
              onClick={() => toggle(item.cr_crm_catalogcacheid)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              title={isFavorite(item.cr_crm_catalogcacheid) ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isFavorite(item.cr_crm_catalogcacheid) ? <StarFilled style={{ color: '#f59e0b' }} /> : <StarRegular />}
            </button>
          )},
          { columnId: 'product', label: 'Product', renderCell: (item) => <span title={item.cr_crm_productname} style={{ minWidth: 220, display: 'inline-block' }}>{item.cr_crm_productname}</span>, compare: (a, b) => a.cr_crm_productname.localeCompare(b.cr_crm_productname) },
          { columnId: 'sku', label: 'SKU', renderCell: (item) => <span title={item.cr_crm_skuname} style={{ minWidth: 260, display: 'inline-block' }}>{item.cr_crm_skuname}</span>, compare: (a, b) => a.cr_crm_skuname.localeCompare(b.cr_crm_skuname) },
          { columnId: 'skuId', label: 'SKU ID', renderCell: (item) => item.cr_crm_skuid, compare: (a, b) => a.cr_crm_skuid.localeCompare(b.cr_crm_skuid) },
          { columnId: 'category', label: 'Category', renderCell: (item) => item.cr_crm_category, compare: (a, b) => a.cr_crm_category.localeCompare(b.cr_crm_category) },
          { columnId: 'billing', label: 'Billing', renderCell: (item) => {
            const label = String(item.cr_crm_billingcycles ?? '').replace(/^./, c => c.toUpperCase());
            return label || '—';
          }, compare: (a, b) => String(a.cr_crm_billingcycles).localeCompare(String(b.cr_crm_billingcycles)) },
          { columnId: 'term', label: 'Term', renderCell: (item) => item.cr_crm_termduration || '—', compare: (a, b) => (a.cr_crm_termduration ?? '').localeCompare(b.cr_crm_termduration ?? '') },
          { columnId: 'price', label: 'ERP Price (USD)', renderCell: (item) => {
            const price = item.cr_crm_erppriceusd;
            if (price > 0) return `$${price.toFixed(2)}`;
            if (price === 0 && item.cr_crm_pricingsynced) return 'Free';
            return '—';
          }, compare: (a, b) => a.cr_crm_erppriceusd - b.cr_crm_erppriceusd },
          { columnId: 'qty', label: 'Min Qty', renderCell: (item) => String(item.cr_crm_minqty ?? 1), compare: (a, b) => (a.cr_crm_minqty ?? 1) - (b.cr_crm_minqty ?? 1) },
          { columnId: 'available', label: 'Available', renderCell: (item) => item.cr_crm_available ? 'Yes' : 'No', compare: (a, b) => Number(b.cr_crm_available) - Number(a.cr_crm_available) },
        ]}
        getRowId={(item) => item.cr_crm_catalogcacheid}
      />
      </div>
    </div>
  );
};
